import type { TelegramMessage } from '@/types/monitor'

export const DISPLAY_TIME_ZONE = 'Asia/Shanghai'
export const DISPLAY_TIME_ZONE_LABEL = 'UTC+8'

function dateTimeParts(value: string | Date): Record<string, string> | null {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      hourCycle: 'h23',
      timeZone: DISPLAY_TIME_ZONE,
    })
      .formatToParts(date)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  )
}

export function formatUtcTime(value: string | Date): string {
  const parts = dateTimeParts(value)
  if (!parts) return '—'
  return `${parts.hour}:${parts.minute}:${parts.second}`
}

export function formatUtcDateTime(value: string | Date): string {
  const parts = dateTimeParts(value)
  if (!parts) return '—'
  return `${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second} ${DISPLAY_TIME_ZONE_LABEL}`
}

export function formatLocalDateTime(value: string | Date): string {
  const parts = dateTimeParts(value)
  if (!parts) return '—'
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`
}

export function compactAddress(value: string): string {
  const normalized = value.trim()
  if (normalized.length <= 10) return normalized
  return `${normalized.slice(0, 6)}…${normalized.slice(-4)}`
}

export function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainder = seconds % 60
  return [hours, minutes, remainder].map((value) => String(value).padStart(2, '0')).join(':')
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function stripMediaPrefix(value: string): string {
  return value.replace(/^\[(?:PHOTO|VIDEO|GIF|DOCUMENT)\]\s*/i, '')
}

export function resolveMediaUrl(message: TelegramMessage): string | null {
  if (message.media_url) return message.media_url
  return resolvePublicMediaPath(message.media_path)
}

export function resolveSenderAvatarUrl(message: TelegramMessage): string | null {
  if (message.sender_avatar_url) return message.sender_avatar_url
  return resolvePublicMediaPath(message.sender_avatar_path)
}

export function resolvePublicMediaPath(path?: string | null): string | null {
  if (!path) return null
  const normalized = path.replaceAll('\\', '/')
  const filename = normalized.split('/').pop()
  return filename ? `/media/${encodeURIComponent(filename)}` : null
}

export function truncate(value: string, length: number): string {
  const characters = Array.from(value)
  return characters.length > length ? `${characters.slice(0, length).join('')}…` : value
}

